import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { OfflineService } from 'src/app/offline/offline.service';
import { HeaderService } from 'src/app/system-common/services/header.service';
import { SettingService } from 'src/app/system-common/services/setting.service';
import * as Chart from 'chart.js';
import { DashboardService } from '../../services/dashboard.service';

@Component({
  selector: '',
  template: '',
})
export class InsurerPremiumChartHandler {
  chart: Chart;
  @Output() closeChart = new EventEmitter<string>();
  pieColorArray = [];
  @Input() usedFor;
  frequencyArr = [
    this.settingService.getLabelValue('monthly'),
    this.settingService.getLabelValue('quarterly'),
    this.settingService.getLabelValue('yearly'),
  ];
  frequency = this.settingService.getLabelValue('yearly');
  selectedCalculationYears: string[] = [];
  selectedFrequencyTemp: string;
  public calculationyears;
  private originalRectangleDraw;
  private userLoginData;

  // Insurer Premium Chart variables
  selectedBrokerFrequencyTemp: string;
  public selectedBrokerPremium: any = '';
  brokerPremiumChart: boolean;
  insurerPremiumRawData;
  public agentInsurerPremiumChannels = [];
  public agentBrokerPremiumLabels = [];
  selectedChannelTemp: string;

  premiumChartData;
  multiLevelLabelPlugin;
  premiumChartOptions;

  constructor(
    public service: DashboardService,
    public headerService: HeaderService,
    public router: Router,
    public settingService: SettingService,
    public offlineService: OfflineService
  ) {
    const dateStr = this.service.getDateFormatted(this.settingService.getSessionStorageValue('BatchDate'));
    const year = parseInt(dateStr.split('/')[2], 10);
    this.calculationyears = [String(year), String(year + 1)];
    this.originalRectangleDraw = Chart.elements.Rectangle.prototype.draw;
    if (sessionStorage.getItem('userLoginData') != undefined) {
      this.userLoginData = JSON.parse(sessionStorage.getItem('userLoginData'));
    }
  }

  ngOnInit() {
    this.loadBarChartDetails();
  }

  public loadBarChartDetails() {
    this.pieColorArray = this.service.getColor();
    this.brokerPremiumChart = false;
    setTimeout(() => {
      this.brokerPremiumChart = true;
      setTimeout(() => {
        this.premiumLInsurerBarChartBrowser();
      }, 10);
    }, 10);
  }

  /* Premium insurer Chart Code Starts Here */
  premiumLInsurerBarChartBrowser(
    insurerPremiumfrequency?: string,
    selectedBrokerPremium?: string[] | string,
    insurerPremiumcalculationYears?: any
  ) {
    this.brokerPremiumChart = true;
    this.frequency = insurerPremiumfrequency || this.frequency || 'YEARLY';

    this.service
      .getPerformanceData(['insurer'], this.frequency, insurerPremiumcalculationYears)
      .subscribe((res) => {
        const rawData = this.extractValidData(res);
        if (!rawData) return;

        this.insurerPremiumRawData = rawData;

        const chartColors = this.service.getColor();
        const insurerPremiumList = Object.keys(this.insurerPremiumRawData);
        this.agentBrokerPremiumLabels = ['All', ...insurerPremiumList];

        const selectedProducts = this.getSelectedProducts(insurerPremiumList, selectedBrokerPremium);

        const { labels, datasets, years, tooltipMeta } = this.prepareChartData(selectedProducts, chartColors);

        this.multiLevelLabelPlugin = this.createMultiLevelLabelPlugin(years);

        this.setChartConfig(labels, datasets, tooltipMeta);
      });
  }

  // Extract and validate the API response
  private extractValidData(res: any): any | null {
    let resData;
    try {
      resData = JSON.parse(JSON.stringify(res));
    } catch {
      console.error('Failed to parse response data');
      return null;
    }

    const rawData = resData?.data?.data?.[0] || {};
    if (!rawData || typeof rawData !== 'object' || Object.keys(rawData).length === 0) {
      console.warn('No valid raw data found');
      return null;
    }

    return rawData;
  }

  // Determine selected products for charting
  private getSelectedProducts(insurerPremiumList: string[], selectedBrokerPremium?: string[] | string): string[] {
    if (this.usedFor === 'performance') {
      selectedBrokerPremium = insurerPremiumList[0];
    } else {
      selectedBrokerPremium = selectedBrokerPremium || insurerPremiumList;
    }

    if (!Array.isArray(selectedBrokerPremium)) {
      selectedBrokerPremium = [selectedBrokerPremium];
    }

    return selectedBrokerPremium.includes('All') || selectedBrokerPremium.length === 0
      ? insurerPremiumList
      : selectedBrokerPremium.filter((p) => insurerPremiumList.includes(p));
  }

  // Prepare labels, datasets, years and tooltip metadata
  private prepareChartData(
    filteredProducts: string[],
    chartColors: string[]
  ): { labels: string[]; datasets: any[]; years: any[]; tooltipMeta: any } {
    const labels: string[] = [];
    const tooltipMeta: { [index: number]: { year: string; period: string } } = {};
    const xMeta: { year: string; period: string }[] = [];
    const datasets: any[] = [];

    filteredProducts.forEach((product, pIndex) => {
      const yearData = this.insurerPremiumRawData[product];
      const dataPoints: number[] = [];
      let i = 0;

      Object.keys(yearData)
        .sort()
        .forEach((year) => {
          const periodData = yearData[year]?.data;
          if (periodData && typeof periodData === 'object') {
            Object.keys(periodData)
              .sort()
              .forEach((period) => {
                if (pIndex === 0) {
                  labels.push(`${period}`);
                  xMeta.push({ year, period });
                  tooltipMeta[i] = { year, period };
                  i++;
                }
                dataPoints.push(periodData[period] ?? 0);
              });
          }
        });

      datasets.push({
        label: product,
        data: dataPoints,
        borderColor: chartColors[pIndex % chartColors.length],
        backgroundColor: chartColors[pIndex % chartColors.length],
        categoryPercentage: dataPoints.length > 2 ? 0.8 : 0.5,
        barPercentage: dataPoints.length > 2 ? 0.5 : 0.4,
        fill: false,
      });
    });

    const years = this.buildYearsGroup(xMeta);

    return { labels, datasets, years, tooltipMeta };
  }

  // Build year groupings for multi-level labels
  private buildYearsGroup(xMeta: { year: string; period: string }[]): any[] {
    const years: any[] = [];
    let currentYear = '';
    let from = 0;

    xMeta.forEach((entry, idx) => {
      if (entry.year !== currentYear) {
        if (currentYear) {
          years.push({ label: currentYear, from, to: idx - 1 });
        }
        currentYear = entry.year;
        from = idx;
      }
    });
    years.push({ label: currentYear, from, to: xMeta.length - 1 });

    return years;
  }

  // Generate the multi-level label plugin
  private createMultiLevelLabelPlugin(years: any[]): any {
    const frq = this.frequency;

    return {
      beforeDraw(chart: any) {
        const ctx = chart.chart.ctx;
        const xAxis = chart.scales['x-axis-0'];

        ctx.save();
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        if (frq != 'YEARLY') {
          xAxis.ticks.forEach((value: string, index: number) => {
            const x = xAxis.getPixelForTick(index);
            const y = chart.chartArea.bottom + 10;
            ctx.save();
            ctx.translate(x, y);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(value, 0, 0);
            ctx.restore();
          });
        }

        years.forEach((y) => {
          const startX = xAxis.getPixelForTick(y.from);
          const endX = xAxis.getPixelForTick(y.to);
          const centerX = (startX + endX) / 2;
          ctx.fillText(y.label, centerX, chart.chartArea.top - 15);

          if (years.length > 1) {
            const separatorX = endX + (xAxis.getPixelForTick(1) - xAxis.getPixelForTick(0)) / 2;
            if (separatorX < chart.chartArea.right) {
              ctx.beginPath();
              ctx.setLineDash([8, 4]);
              ctx.moveTo(separatorX, chart.chartArea.top);
              ctx.lineTo(separatorX, chart.chartArea.bottom);
              ctx.strokeStyle = '#888';
              ctx.stroke();
              ctx.setLineDash([]);
            }
          }
        });

        ctx.restore();
      },
    };
  }

  // Configure and set the chart options
  private setChartConfig(labels: string[], datasets: any[], tooltipMeta: any): void {
    const commonFontSettings = {
      fontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
      fontColor: '#000000',
    };

    this.premiumChartData = { labels, datasets };

    this.premiumChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 30, bottom: 20 } },
      hover: { mode: 'nearest', intersect: true },
      scales: {
        xAxes: [
          {
            ticks: {
              display: false,
              autoSkip: false,
              ...commonFontSettings,
            },
            gridLines: { display: false },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (val) => val.toLocaleString(),
              ...commonFontSettings,
            },
          },
        ],
      },
      legend: {
        display: this.usedFor !== 'performance',
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 12,
          fontSize: this.settingService.isMobileViewRendered() ? 8 : 12,
          padding: 30,
          ...commonFontSettings,
        },
      },
      tooltips: {
        mode: 'nearest',
        intersect: true,
        bodyFontFamily: commonFontSettings.fontFamily,
        callbacks: {
          title: (tooltipItems) => {
            const idx = tooltipItems[0].index;
            const meta = tooltipMeta[idx];
            return `${meta.period}, ${meta.year}`;
          },
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            const value = dataset?.data?.[tooltipItem.index];
            const label = dataset?.label || '';
            const formatted = Number(value || 0).toLocaleString();
            return `${label}: ${formatted}`;
          },
        },
      },
      plugins: {
        datalabels: false,
      },
    };
  }

  onFilterBrokerPremiumData() {
    $('#agentBrokerPremiumWiseStatusModal').toggle();
  }

  closeBrokerPremiumModal() {
    $('#agentBrokerPremiumWiseStatusModal').hide();
  }

  onDateRangeBrokerPremiumChange(event) {
    this.service.onDateRangeChange(event);
    this.brokerPremiumChart = false;

    setTimeout(() => {
      this.brokerPremiumChart = true;
      setTimeout(() => {
        this.premiumLInsurerBarChartBrowser(
          this.frequency,
          this.selectedBrokerPremium,
          this.selectedCalculationYears
        );
      }, 10);
    }, 10);
  }

  updateInsurerPremiumListForSelectedChannel() {
    if (!this.insurerPremiumRawData || !this.selectedChannelTemp) {
      this.agentBrokerPremiumLabels = [];
      return;
    }

    const channelData = this.insurerPremiumRawData[this.selectedChannelTemp];
    if (!channelData) {
      this.agentBrokerPremiumLabels = [];
      return;
    }

    const products = Object.keys(channelData);
    this.agentBrokerPremiumLabels = ['All', ...products];
  }

  filterBrokerPremium() {
    const products = this.selectedBrokerPremium;
    const frequency = this.selectedBrokerFrequencyTemp;
    this.frequency = frequency || this.frequency;

    this.brokerPremiumChart = false;
    setTimeout(() => {
      this.brokerPremiumChart = true;
      setTimeout(() => {
        this.premiumLInsurerBarChartBrowser(this.frequency, products, this.selectedCalculationYears);
      }, 10);
    }, 10);
    this.closeBrokerPremiumModal();
  }

  // Close the Chart on click of the close button
  onCloseClick() {
    this.closeChart.emit(this.chart);
  }

  clearData() {
    this.selectedBrokerPremium = [];
    this.selectedBrokerFrequencyTemp = '';
    this.selectedCalculationYears = [];
    this.frequency = this.settingService.getLabelValue('yearly');
    this.selectedChannelTemp = '';

    $('#agentBrokerPremiumWiseStatusModal').hide();

    this.brokerPremiumChart = false;
    if (this.chart) {
      this.chart.destroy();
    }

    this.premiumChartData = null;
    this.premiumChartOptions = null;
    this.agentBrokerPremiumLabels = [];
    this.insurerPremiumRawData = {};

    setTimeout(() => {
      this.brokerPremiumChart = true;
      setTimeout(() => {
        this.premiumLInsurerBarChartBrowser(
          this.frequency,
          this.selectedBrokerPremium,
          this.selectedCalculationYears
        );
      }, 10);
    }, 10);
  }

  ngOnDestroy() {
    this.service.clear();
    Chart.elements.Rectangle.prototype.draw = this.originalRectangleDraw;
  }
}
